import { useRef, useState } from 'react';
import { TFunction } from 'i18next';

interface useUploadDroppedVideoProps {
  t: TFunction<undefined>;
  allowedFormats?: string[];
  maximumVideoSize?: number;
}

export const useUploadDroppedVideo = ({
  t,
  allowedFormats = ['mp4', 'mov', 'avi'],
  maximumVideoSize,
}: useUploadDroppedVideoProps) => {
  const [uploadedVideo, setUploadedVideo] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const chooseFileInputRef = useRef<HTMLInputElement | null>(null);

  /**
   * This function is used to ensure the error message state triggers everytime it should,
   * even though the same state is used again
   */
  const showErrorMessage = (message: string) => {
    // Reset error messages state
    setErrorMessage('');
    // Use setTimeout to ensure the state update happens in the next event loop
    setTimeout(() => {
      setErrorMessage(message);
    }, 0);
  };

  const isFormatAllowed = (file: File) => {
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!fileExtension || !allowedFormats.includes(fileExtension)) {
      const formatsText = allowedFormats.join(
        `, ${t('common:or').toLowerCase()} `
      );
      showErrorMessage(
        `${t('common:formatNotAllowedHereIsTheAllowedFormat')} : ${formatsText}`
      );
      return false;
    }
    return true;
  };

  const handleFileValidation = (file: File) => {
    setErrorMessage('');

    if (!isFormatAllowed(file)) {
      return false;
    }

    if (maximumVideoSize && file.size > maximumVideoSize * 1024 * 1024) {
      showErrorMessage(
        `${t('common:maxAllowedWeight')} : ${maximumVideoSize} ${t(
          'common:mb'
        )}`
      );
      return false;
    }

    return true;
  };

  const handleFileDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleFileDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file && file.type.startsWith('video/')) {
      const isValid = handleFileValidation(file);
      if (isValid) {
        setUploadedVideo(URL.createObjectURL(file));
      }
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith('video/')) {
      const isValid = handleFileValidation(file);
      if (isValid) {
        setUploadedVideo(URL.createObjectURL(file));
      }
    }
  };

  const handleClickChooseVideo = () => {
    chooseFileInputRef.current?.click();
  };

  return {
    uploadedVideo,
    errorMessage,
    chooseFileInputRef,
    setUploadedVideo,
    handleFileDragOver,
    handleFileDrop,
    handleFileChange,
    handleClickChooseVideo,
  };
};
